import { useState, useEffect } from "react";
import { DeviceRecord, Device, DeviceType } from "../../Types";
import { useGlobalContext } from "../../Context/GlobalContext";
import { Grid2 as Grid } from '@mui/material';
import { Form } from "../../Components/form";
import Product from '../../Components/product';
import Notify from "../../Utils/Notify";
import { defaultProfile } from "../../Types";
import { compatibleEbike, hasCobberSettings } from "../../Lib/Devices";

const AddDevice = (
    {setView, handleExisting, setSelectedDeviceID}: 
    {setView: Function, handleExisting: Function, setSelectedDeviceID: Function}
) => {
    const { dbManager, deviceManager, usbManager, showPopup, hidePopup } = useGlobalContext();

    const [loading, setLoading] = useState<boolean>(false);
    const [config, setConfig] = useState<Device | null>(null);
    const [name, setName] = useState<string>('');
    const [isElectricBike, setIsElectricBike] = useState<boolean>(false);
    const [make, setMake] = useState<any>({});
    const [powerIdentifier, setPowerIdentifier] = useState<string>('');
    const [initialised, setInitialised] = useState<boolean>(false);
    const [powerOptions, setPowerOptions] = useState<any[]>([]);
    const [allMakes, setAllMakes] = useState<any[]>([]);
    const [allModels, setAllModels] = useState<any[]>([]);
    const [voltage, setVoltage] = useState<string>('');
    const [power, setPower] = useState<string>('');
    const [voltageOptions, setVoltageOptions] = useState<any[]>([]);
    const [wattageOptions, setWattageOptions] = useState<any[]>([]);
    const [types, setTypes] = useState<DeviceType[]>([]);

    useEffect(() => {
        const init = async () => {
            const options = await dbManager.request('/items/options');

            setVoltageOptions(options.other_ebike_voltages.map((item: any) => ({key: `${item}V`, value: item})));
            setWattageOptions(options.other_ebike_powers.map((item: any) => ({key: `${item}W`, value: item})));
            setTypes(
                (await deviceManager.getDeviceTypes())
                    .filter(
                    (d: DeviceType) =>
                        d.status === 'published' || d.status === 'coming_soon'
                    )
                    .sort((a: DeviceType, b: DeviceType) => {
                    const aIs = a.status === 'coming_soon';
                    const bIs = b.status === 'coming_soon';

                    if (aIs !== bIs) {
                        return aIs ? 1 : -1;
                    }

                    return a.name.localeCompare(b.name);
                })
            );
        }
        if(!initialised) {
            setInitialised(true);
            init();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usbManager.selectedDevice]);

    const matchType = async (type: DeviceType) => {
        if(type.status === 'coming_soon') return;
        // Change to better system later for onboarding
        if(hasCobberSettings(type.id)) {
            showPopup(
                {
                    title: "",
                    content: <div style={{padding: '0 10px', lineHeight: '20px', textWrap: 'balance'}}>
                        <p>1. PLEASE ENSURE YOUR COBBER REFLEX IS CONNECTED VIA USB.</p>
                        <p>2. PRESS AND HOLD THE BUTTON ON YOUR COBBER REFLEX FOR IT TO SHOW IN THE BROWSER POP-UP.</p> 
                        <p>3. SELECT THE COBBER AND CLICK CONNECT TO CONTINUE.</p>
                    </div>,
                    closeIcon: true,
                    noButtons: true
                }
            )
        }

        if(await usbManager.getDevices()) {
            hidePopup()
            const device = await deviceManager.getDeviceBySerial(usbManager.selectedDeviceId.toString());
            if(device) {
                handleExisting(device);
                return;
            }   
            const type = await deviceManager.getType(usbManager.selectedDeviceType);
            if(!type) {
                Notify('error', 'This device is not a compatible Knog light');
                return;
            }

            usbManager.registerEvent('disconnect', () => setConfig(null));

            setConfig({
                id: 0,
                name: type.name,
                type: type,
                profiles: []
            });

            setAllMakes([...(await dbManager.request('/items/bike_makes')), {id: 0, name: 'Other'} ]);
            setPowerOptions(await dbManager.request(`/items/bike_power`));
        } else {
            hidePopup()
        }
    }

    const handleSubmit = async () => {
        if(loading) return;
        
        const deviceRecord: DeviceRecord = {
            owner: dbManager!.user!.id!,
            type: config!.type.id,
            name: name,
            hardware_id: usbManager.selectedDeviceId.toString(),
            properties: { uploadedProfile: defaultProfile.id, synced: true },
            profiles: [ 
                {
                    ...defaultProfile, 
                    settings: {
                        smartLighting: config?.type.default_smart_lighting,
                        accel_threshold: config?.type.sensitivities?.accel_range[config?.type.sensitivities.default],
                        sleep_threshold: config?.type.sensitivities?.sleep_range[config?.type.sensitivities.default],
                        min_time_before_wake: config?.type.default_wake,
                        min_time_before_sleep: config?.type.default_sleep,
                    },
                    ...(isElectricBike ? {settings: { 
                        ebike_power_id: make !== 0 ? powerIdentifier : 0,
                        customVoltage: voltage,
                        customPower: power,
                    }} : {}),
                    flash_modes: await deviceManager.getDefaultModes(config!.type)
                } 
            ],
        };

        setLoading(true);
        setSelectedDeviceID((await deviceManager.addDevice(deviceRecord)).data.id);
        setView('editDevice');
    }

    const handleSelectMake = async (value: string) => {
        setMake(value);
        setAllModels(powerOptions.filter((option) => option.make === value).map((item) => ({ key: item.model, value: item.identifier })))
    }

    const formFields = [
        {
            label: "PRODUCT NICKNAME",
            type: "text",
            placeholder: "Enter product",
            setValue: (value: string) => setName(value),
            value: name
        },
        ...(compatibleEbike(config?.type.id ?? 0) ? [{
            label: "Are you connecting this light to an e-Bike?",
            type: "toggle",
            setValue: (value: boolean) => setIsElectricBike(value),
            value: isElectricBike
            }] : []),
        ...(isElectricBike ? [{
            label: "MAKE OF E BIKE MOTOR",
            type: "select",
            placeholder: "Select make",
            setValue: handleSelectMake,
            value: make,
            values: allMakes.map((item) => ({ key: item.name, value: item.id })),
        },
        ...(make !== 0 ? [{
            label: "MODEL OF E BIKE MOTOR",
            type: "select",
            placeholder: "Enter bike", 
            setValue: (value: string) => setPowerIdentifier(value),
            value: powerIdentifier,
            values: allModels,
        }] : [
            {
                label: "VOLTAGE",
                type: "select",
                placeholder: "Select voltage", 
                setValue: (value: string) => setVoltage(value),
                value: voltage,
                values: voltageOptions
            },
            {
                label: "POWER",
                type: "select",
                placeholder: "Select power", 
                setValue: (value: string) => setPower(value),
                value: power,
                values: wattageOptions
            }
        ])] : []),
    ];

    return (
        <div>
            {config ? 
                <div className="add-product">
                <Grid container spacing={5} sx={{width: '50%'}}>
                    <Grid size={{xs: 6}}>
                        <Product type={config.type} gifMode={'on'}/>
                        <div style={{marginTop: '10px', fontSize: '24px'}}>{config.type.name?.toUpperCase()}</div>
                    </Grid>
                    <Grid size={{xs: 6}}>
                        <div className="product-form">
                            <Form
                                fields={formFields}
                                submit={handleSubmit}
                                buttonText="Add Product"
                                buttonClass="product-button"
                                buttonWrapperClass="product-button-wrapper"
                                loading={loading}
                            />
                        </div>
                    </Grid>
                </Grid>
                </div>
                :
                <div>
                    <div style={{fontSize: '1.2vw', textAlign: 'center'}}>PLEASE SELECT THE LIGHT YOU WOULD LIKE TO CUSTOMISE.</div>
                    <Grid container sx={{padding: '50px', maxHeight: '75vh', overflow: 'auto'}}>
                        {
                            types.map((t: DeviceType) => {
                                return (
                                    <Grid size={{xs: 3}} onClick={() => matchType(t)} style={{cursor: 'pointer'}} key={t.id}>
                                        <Product type={t}/>
                                        <h2 style={{ fontSize: '22px', marginBottom: '10px' }}>{t.name}</h2>
                                    </Grid>
                                )
                            })
                        }
                    </Grid>              
                </div>
            }   
        </div>
    );
}

export default AddDevice;